import { useAuth } from '../contexts/AuthContext';

export interface SubscriptionInfo {
  tier: 'free' | 'party-pass' | 'pro-subscription';
  isActive: boolean;
  timeRemaining: string | null;
  expiresAt: Date | null;
  features: string[];
  canCreateParties: boolean;
  maxQueueSize: number;
  hasAds: boolean;
}

export const useSubscription = (): SubscriptionInfo => {
  const { userData } = useAuth();

  const isActive = (subscription: { active: boolean; expiresAt: Date } | undefined) => {
    if (!subscription?.active) return false;
    return subscription.expiresAt != null && subscription.expiresAt > new Date();
  };

  const calculateTimeRemaining = (expiresAt: Date | undefined) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const diff = expiresAt.getTime() - now.getTime();
    
    if (diff <= 0) return null;
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  // Check Party Pass (premium tier)
  if (userData?.partyPass && isActive(userData.partyPass)) {
    return {
      tier: 'party-pass',
      isActive: true,
      timeRemaining: calculateTimeRemaining(userData.partyPass.expiresAt),
      expiresAt: userData.partyPass.expiresAt,
      features: [
        '12-hour access',
        'Unlimited queue size',
        'No advertisements',
        'Custom party branding',
        'Priority support'
      ],
      canCreateParties: true,
      maxQueueSize: -1, // Unlimited
      hasAds: false
    };
  }

  // Check Pro Subscription (recurring premium)
  if (userData?.proSubscription && isActive(userData.proSubscription)) {
    return {
      tier: 'pro-subscription',
      isActive: true,
      timeRemaining: calculateTimeRemaining(userData.proSubscription.expiresAt),
      expiresAt: userData.proSubscription.expiresAt,
      features: [
        'Unlimited parties',
        'Unlimited queue size',
        'No advertisements',
        'Custom branding & themes',
        'Priority support'
      ],
      canCreateParties: true,
      maxQueueSize: -1,
      hasAds: false
    };
  }

  // Default to free tier
  return {
    tier: 'free',
    isActive: true,
    timeRemaining: null,
    expiresAt: null,
    features: [
      'Up to 5 songs in queue',
      '1 hour sessions',
      'Basic features',
      'Community support'
    ],
    canCreateParties: true,
    maxQueueSize: 5,
    hasAds: true
  };
};

export const useYouTubeConnection = () => {
  const { userData } = useAuth();

  return {
    isConnected: !!userData?.youtubeToken,
    hasUnlimitedAccess: !!userData?.youtubeToken,
    needsConnection: !userData?.youtubeToken
  };
};